import {
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../primitives/button';
import { Sheet, SheetContent, SheetTitle } from '../primitives/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../primitives/tooltip';
import { cn } from '../lib/cn';
import { ContextPanelProvider, useContextPanelSlot } from './context-panel';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Unread count or similar */
  badge?: number;
  /** Starts a new group with a divider above it */
  group?: boolean;
  /** Sub-pages listed under the item behind a thin line; hidden while the sidebar is collapsed */
  children?: { label: string; href: string }[];
}

/** Texts the layout itself shows; pass translated ones */
export interface AppLayoutLabels {
  nav: string;
  menu: string;
  openMenu: string;
  collapseSidebar: string;
  expandSidebar: string;
  /** Accessible name of the right-hand column */
  contextPanel: string;
  collapsePanel: string;
  expandPanel: string;
}

const defaultLabels: AppLayoutLabels = {
  nav: 'Main',
  menu: 'Menu',
  openMenu: 'Open menu',
  collapseSidebar: 'Collapse sidebar',
  expandSidebar: 'Expand sidebar',
  contextPanel: 'Details',
  collapsePanel: 'Hide details panel',
  expandPanel: 'Show details panel',
};

interface AppLayoutProps {
  brand: ReactNode;
  /** Shown instead of `brand` when the sidebar is collapsed, e.g. a logo mark */
  brandCollapsed?: ReactNode;
  /** Small label under the logo, e.g. "Provider" or "Admin" */
  areaLabel?: string;
  nav: NavItem[];
  activeHref: string;
  /** Renders a nav link; pass the router's Link so navigation stays in the app */
  renderLink: (item: NavItem, content: ReactNode, className: string) => ReactNode;
  /** The page name; read out to screen readers in the header (the page itself shows it) */
  title: string;
  /** Left of the header, e.g. `HeaderSearch` */
  search?: ReactNode;
  /** Right side of the header: language, notifications, account menu */
  headerActions?: ReactNode;
  /** Bottom of the sidebar: e.g. a help card or log out. A function gets whether the sidebar is collapsed. */
  sidebarFooter?: ReactNode | ((collapsed: boolean) => ReactNode);
  /** Desktop sidebar shows icons only. Pass with `onCollapsedChange` to remember it; otherwise it starts expanded. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Right-hand column shrinks to a thin strip with a show button. Pass with `onPanelCollapsedChange` to remember it. */
  panelCollapsed?: boolean;
  onPanelCollapsedChange?: (collapsed: boolean) => void;
  labels?: Partial<AppLayoutLabels>;
  children: ReactNode;
}

function formatBadge(count: number) {
  return count > 99 ? '99+' : String(count);
}

/**
 * The dashboard frame: a light sidebar that collapses to icons (a slide-in menu on phones), a quiet header, the
 * page, and from `xl` a white right-hand column for the page's ContextPanel.
 */
export function AppLayout({
  brand,
  brandCollapsed,
  areaLabel,
  nav,
  activeHref,
  renderLink,
  title,
  search,
  headerActions,
  sidebarFooter,
  collapsed: collapsedProp,
  onCollapsedChange,
  panelCollapsed: panelCollapsedProp,
  onPanelCollapsedChange,
  labels: labelsProp,
  children,
}: AppLayoutProps) {
  const labels = { ...defaultLabels, ...labelsProp };
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsedState, setCollapsedState] = useState(false);
  const collapsed = collapsedProp ?? collapsedState;
  const [panelCollapsedState, setPanelCollapsedState] = useState(false);
  const panelCollapsed = panelCollapsedProp ?? panelCollapsedState;

  const togglePanel = () => {
    setPanelCollapsedState(!panelCollapsed);
    onPanelCollapsedChange?.(!panelCollapsed);
  };
  const panel = useContextPanelSlot();
  const panelToggleLabel = panelCollapsed ? labels.expandPanel : labels.collapsePanel;
  const PanelToggleIcon = panelCollapsed ? PanelRightOpen : PanelRightClose;

  const toggleCollapsed = () => {
    setCollapsedState(!collapsed);
    onCollapsedChange?.(!collapsed);
  };

  // The phone menu always shows labels; `iconsOnly` is only true for the collapsed desktop sidebar
  const renderNav = (iconsOnly: boolean) => (
    <nav aria-label={labels.nav} className="grid gap-0.5 tall:gap-1.5">
      {nav.map((item) => {
        const active = item.href === activeHref;
        const className = cn(
          'group relative flex h-9 items-center gap-3 rounded-xl px-3 text-[0.8125rem] font-medium transition-colors tall:h-11 tall:text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          iconsOnly && 'mx-auto size-10 justify-center px-0 tall:size-11',
          active
            ? 'bg-secondary font-semibold text-secondary-foreground shadow-md shadow-secondary/20'
            : 'text-muted-foreground hover:bg-muted hover:text-secondary',
        );
        const badge = (count: number, extra?: string) => (
          <span
            className={cn(
              'min-w-5 rounded-md bg-primary px-1.5 text-center font-semibold text-primary-foreground tabular-nums',
              extra,
            )}
          >
            {formatBadge(count)}
          </span>
        );
        const content = (
          <>
            <span className="relative shrink-0">
              <item.icon
                className={cn('size-[1.125rem] tall:size-5', active && 'text-primary')}
                aria-hidden
              />
              {iconsOnly && item.badge
                ? badge(item.badge, 'absolute -right-2.5 -top-2 min-w-4 px-1 text-[0.625rem] leading-4')
                : null}
            </span>
            <span className={cn('flex-1 truncate whitespace-nowrap', iconsOnly && 'sr-only')}>
              {item.label}
            </span>
            {!iconsOnly && item.badge ? badge(item.badge, 'py-0.5 text-xs leading-4') : null}
          </>
        );
        const subItems =
          !iconsOnly && item.children?.length ? (
            <ul className="ml-5 my-0.5 grid gap-0.5 border-l border-border pl-5">
              {item.children.map((child) => (
                <li key={child.href}>
                  {renderLink(
                    { ...child, icon: item.icon },
                    <span className="truncate">{child.label}</span>,
                    cn(
                      'flex h-7 items-center rounded-lg px-2 text-[0.8125rem] transition-colors tall:h-9 tall:text-sm',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      child.href === activeHref
                        ? 'font-semibold text-secondary'
                        : 'text-muted-foreground hover:text-secondary',
                    ),
                  )}
                </li>
              ))}
            </ul>
          ) : null;
        const link = (
          <div key={item.href} onClick={() => setMenuOpen(false)}>
            {item.group && <hr aria-hidden className="my-2 border-border tall:my-3" />}
            {renderLink(item, content, className)}
            {subItems}
          </div>
        );
        if (!iconsOnly) return link;
        return (
          <Tooltip key={item.href}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">
              {item.label}
              {item.badge ? ` (${formatBadge(item.badge)})` : ''}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </nav>
  );

  const renderSidebar = (iconsOnly: boolean, toggle?: ReactNode) => (
    <div className="flex h-full flex-col gap-4 tall:gap-6">
      <div
        className={cn('flex min-h-10 items-center gap-2 px-2 tall:pt-2', iconsOnly && 'justify-center px-0')}
      >
        {iconsOnly ? brandCollapsed : brand}
      </div>
      {areaLabel && !iconsOnly && (
        <span className="-mt-2 self-start rounded-md bg-muted px-2.5 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {areaLabel}
        </span>
      )}
      <div
        className={cn('flex-1 overflow-x-hidden overflow-y-auto', iconsOnly ? '-mx-4 px-4' : '-mx-1 px-1')}
      >
        {renderNav(iconsOnly)}
      </div>
      {typeof sidebarFooter === 'function' ? sidebarFooter(iconsOnly) : sidebarFooter}
      {toggle}
    </div>
  );

  const toggleLabel = collapsed ? labels.expandSidebar : labels.collapseSidebar;
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const toggle = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('shrink-0 text-muted-foreground', collapsed ? 'self-center' : 'self-end')}
          aria-label={toggleLabel}
          aria-expanded={!collapsed}
          aria-controls="app-sidebar"
          onClick={toggleCollapsed}
        >
          <ToggleIcon />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">{toggleLabel}</TooltipContent>
    </Tooltip>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex min-h-dvh bg-background">
        <aside
          id="app-sidebar"
          data-collapsed={collapsed}
          className={cn(
            'sticky top-0 hidden h-dvh shrink-0 overflow-hidden border-r border-border/70 bg-surface p-4 short:py-3 lg:block',
            'transition-[width] duration-200 ease-out motion-reduce:transition-none',
            collapsed ? 'w-20' : 'w-64',
          )}
        >
          {renderSidebar(collapsed, toggle)}
        </aside>
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" className="w-72 bg-surface p-4">
            <SheetTitle className="sr-only">{labels.menu}</SheetTitle>
            {renderSidebar(false)}
          </SheetContent>
        </Sheet>
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-3 bg-background/85 px-4 backdrop-blur-md sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label={labels.openMenu}
              onClick={() => setMenuOpen(true)}
            >
              <Menu />
            </Button>
            <h2 className="sr-only">{title}</h2>
            <div className="flex min-w-0 flex-1 items-center">{search}</div>
            <div className="flex items-center gap-1.5 sm:gap-2">{headerActions}</div>
          </header>
          <main className="min-w-0 flex-1 px-4 pb-6 pt-1 sm:px-6">
            <ContextPanelProvider slot={panel.slot}>{children}</ContextPanelProvider>
          </main>
        </div>
        {/* Full height on the right, like the sidebar on the left */}
        {panel.hasPanel && panel.wide && (
          <aside
            aria-label={labels.contextPanel}
            data-collapsed={panelCollapsed}
            className={cn(
              'sticky top-0 flex h-dvh shrink-0 flex-col overflow-hidden border-l border-border/70 bg-surface',
              'transition-[width] duration-200 ease-out motion-reduce:transition-none',
              panelCollapsed ? 'w-12' : 'w-80 2xl:w-96',
            )}
          >
            <div className={cn('flex pt-3', panelCollapsed ? 'justify-center px-1' : 'justify-start px-3')}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-muted-foreground"
                    aria-label={panelToggleLabel}
                    aria-expanded={!panelCollapsed}
                    aria-controls="app-context-panel"
                    onClick={togglePanel}
                  >
                    <PanelToggleIcon />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">{panelToggleLabel}</TooltipContent>
              </Tooltip>
            </div>
            {/* Stays mounted while collapsed so the page's portal keeps its target */}
            <div
              id="app-context-panel"
              ref={panel.setTarget}
              hidden={panelCollapsed}
              className="grid min-h-0 flex-1 auto-rows-max content-start gap-4 overflow-y-auto p-5 pt-2"
            />
          </aside>
        )}
      </div>
    </TooltipProvider>
  );
}
